import React, { ReactNode, useEffect, useState } from "react";
import { Grid, Box, GridJustification, Hidden, CircularProgress, Typography, Checkbox } from "@material-ui/core";

export type columns = boolean | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 | "auto" | undefined;

export interface GridableProps<T> {
    items: T[];
    columns: GridableColumn<T>[];
    empty?: string;
    loading: boolean;
    error?: string;
    selectable?: boolean;
    mapper?(item: T): string;
    onChange?(ids: string[], items: T[]): any;
}

export interface GridableColumn<T> {
    title: string | ReactNode;
    converter(item: T, index: number): ReactNode;
    justify?: GridJustification;
    xs?: columns;
    sm?: columns;
    md?: columns;
    lg?: columns;
    xl?: columns;
    fullWidth?: boolean;
}

export default function Gridable<T>(props: GridableProps<T>) {
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [selectedItems, setSelectedItems] = useState<T[]>([]);

    const onChange = (item: T): any => {
        const id = props.mapper!(item);
        const ids = selectedIds.length === 0 ? ([] as string[]) : selectedIds.join(",").split(",");
        const items = selectedItems.map(item => item);

        const index = selectedIds.indexOf(id);
        if (index < 0) {
            ids.push(id);
            items.push(item);
        } else {
            ids.splice(index, 1);
            items.splice(index, 1);
        }

        setSelectedIds(ids);
        setSelectedItems(items);
    };

    const onChangeAll = (checked: boolean): any => {
        var ids: string[], items: T[];
        if (checked) {
            ids = props.items.map(item => props.mapper!(item));
            items = props.items;
        } else {
            ids = [];
            items = [];
        }

        setSelectedIds(ids);
        setSelectedItems(items);
    };

    useEffect(() => {
        if (props.selectable && props.onChange) {
            props.onChange(selectedIds, selectedItems);
        }
        // eslint-disable-next-line
    }, [props.selectable, selectedIds, selectedItems]);

    const gridableBody = (props: GridableProps<T>): any => {
        if (props.loading) {
            return genericRow(<CircularProgress size="1rem" color="secondary" />);
        }

        if (props.error) {
            return genericRow(<Typography variant="caption" color="error">{props.error}</Typography>);
        }

        if (props.items.length === 0 && props.empty) {
            return genericRow(<Typography variant="caption">{props.empty}</Typography>);
        }

        return (
            <Grid item xs={12}>
                {props.items.map((item: T, indexRow: number) => (
                    <Grid key={indexRow} container className="GridableRow">
                        {props.columns.map((column: GridableColumn<T>, indexColumn: number) => (
                            <GridableCell key={indexColumn} column={column} item={item} index={indexColumn}
                                selectable={props.selectable && indexColumn === 0} mapper={props.mapper}
                                total={props.items.length} selected={selectedIds}
                                onChange={onChange} onChangeAll={onChangeAll} />
                        ))}
                    </Grid>
                ))}
            </Grid>
        );
    };

    const genericRow = (child: ReactNode) => {
        return (
            <Grid item xs={12}>
                <Grid container justify="center" className="GridableRow">
                    <Grid item xs="auto">
                        <Box py={1}>{child}</Box>
                    </Grid>
                </Grid>
            </Grid>
        );
    };

    return (
        <Grid container className="Gridable">
            <Grid item xs={12}>
                <Grid container className="GridableRow GridableHeader">
                    {props.columns.map((column: GridableColumn<T>, index: number) => (
                        <GridableCell key={index} column={column} index={index}
                            selectable={props.selectable && index === 0} mapper={props.mapper}
                            total={props.items.length} selected={selectedIds}
                            onChange={onChange} onChangeAll={onChangeAll} />
                    ))}
                </Grid>
            </Grid>
            <Grid item xs={12}>
                {gridableBody(props)}
            </Grid>
        </Grid>
    );
}

interface GridableCellProps<T> {
    column: GridableColumn<T>;
    item?: T;
    index: number;
    total: number;
    selected: string[];
    selectable?: boolean;
    mapper?(item: T): string;
    onChange(item: T): any;
    onChangeAll(checked: boolean): any;
}

function GridableCell<T>(props: GridableCellProps<T>) {

    const value = (item: T): ReactNode => {
        const id = props.mapper ? props.mapper(item) : "";
        return (
            <Grid item xs={12}>
                <Grid container justify="flex-start" alignItems="center" alignContent="center" spacing={1}>
                    {props.selectable && (
                        <Grid item xs="auto">
                            <Checkbox
                                id={id}
                                name={id}
                                value={id}
                                checked={props.selected.indexOf(id) >= 0}
                                onChange={handleSelection}
                                className="GridableCheck"
                            />
                        </Grid>
                    )}
                    <Grid item xs>
                        {props.column.converter(item, props.index)}
                    </Grid>
                </Grid>
            </Grid>
        );
    };

    const title = (): ReactNode => {
        return (
            <Grid item xs={12}>
                <Grid container justify="flex-start" alignItems="center" alignContent="center" spacing={1}>
                    {props.selectable && (
                        <Grid item xs="auto">
                            <Checkbox
                                id="gridable-all"
                                name="gridable-all"
                                checked={props.selected.length > 0 && props.selected.length === props.total}
                                indeterminate={props.selected.length > 0 && props.selected.length < props.total}
                                onChange={handleAllCheckboxs}
                                disabled={props.total === 0}
                                className="GridableCheck"
                            />
                        </Grid>
                    )}
                    <Grid item xs>
                        {!props.item && (typeof props.column.title === "string") && (<strong>{props.column.title}</strong>)}
                        {!props.item && (typeof props.column.title !== "string") && (<div>{props.column.title}</div>)}
                    </Grid>
                </Grid>
            </Grid>
        );
    };

    const content = (): ReactNode => {
        if (props.item) {
            return value(props.item);
        }
        return title();
    };

    const handleAllCheckboxs = (_event: React.ChangeEvent<{}>, checked: boolean) => props.onChangeAll(checked);

    const handleSelection = (event: React.ChangeEvent<HTMLInputElement>) => props.onChange(props.item!);

    return (
        <Hidden
            xsDown={props.column.xs === false}
            smDown={props.column.sm === false}
            mdDown={props.column.md === false}
            lgDown={props.column.lg === false}
            xlDown={props.column.xl === false}>
            <Grid item xs={props.column.xs} sm={props.column.sm} md={props.column.md} lg={props.column.lg} xl={props.column.xl}>
                <Box px={2} display="flex" alignItems="center" justifyContent={props.column.justify || "flex-start"} className="GridableCell">
                    <Box textOverflow="ellipsis"
                        overflow={props.column.fullWidth === undefined || props.column.fullWidth ? undefined : "auto hidden"}
                        width={props.column.fullWidth === undefined || props.column.fullWidth ? "100%" : undefined}>
                        {content()}
                    </Box>
                </Box>
            </Grid>
        </Hidden>
    );
}